import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { useMMMonitoring } from "../../hooks/api/admin/useMMMonitoring";
import { SPACING } from "../../constants/design/spacing";
import useScreenSize from "../../hooks/api/screenSize/useScreenSize";
import { MobileDirectory } from "../shared/MobileDirectory";
import { Spinner } from "../shared/Spinner";
import { useBinancePositions } from "../../hooks/api/binance/useBinancePositions";
import { useBybitPositions } from "../../hooks/api/bybit/useBybitPositions";
import { useDeribitPositions } from "../../hooks/api/deribit/useDeribitPositions";
import { useHyperliquidPositions } from "../../hooks/api/hyperliquid/useHyperliquidPositions";
import { LighterCoverage } from "../LighterCoverage";
import { useLighterPositions } from "../../hooks/api/lighter/useLighterPositions";
import { useDeribitAccount } from "../../hooks/api/deribit/useDeribitAccount";
import { INTERNAL_PERPS_SHORT_ADDRESS, INTERNAL_PERPS_LONG_ADDRESS, INTERNAL_OPTIONS_ADDRESS } from "../../constants/admin";
import { PerpAggregatedPositionTable } from "../Account/tables/PerpAggregatedPositionTable";
import { OptionsAggregatedPositionTable } from "../Account/tables/OptionsAggregatedPositionTable";
import { OptionsGreeksSummaryTable } from "./OptionsGreeksSummaryTable";
import { useAccount } from "../../hooks/api/admin/useAccount";
import { standardizeBinancePositions } from "../../utils/positions/binance";
import { standardizeBybitPositions } from "../../utils/positions/bybit";
import { standardizeAevoPositions } from "../../utils/positions/aevo";
import { standardizeHyperliquidPositions } from "../../utils/positions/hyperliquid";
import { standardizeLighterPositions } from "../../utils/positions/lighter";
import { aggregatePositions } from "../../utils/positions";

const ENV_OPTIONS = [
  { value: "testnet", label: "testnet" },
  { value: "mainnet", label: "mainnet" },
];

interface IProcessStatus {
  process: string;
  total_assets: number;
  total_instances: number;
  assets: Array<{
    asset: string;
    status: string;
    count: number;
  }>;
}

export function MMMonitoring() {
  const { isMobileScreen } = useScreenSize();
  const { getProcessStatus } = useMMMonitoring();

  const [selectedStatusEnv, setSelectedStatusEnv] = useState<string>(
    ENV_OPTIONS[1].value
  );
  const [perpMm1Status, setPerpMm1Status] = useState<IProcessStatus | null>(null);
  const [perpMm2Status, setPerpMm2Status] = useState<IProcessStatus | null>(null);
  const [perpHedger1Status, setPerpHedger1Status] = useState<IProcessStatus | null>(null);
  const [perpHedger2Status, setPerpHedger2Status] = useState<IProcessStatus | null>(null);
  const [optionsMmStatus, setOptionsMmStatus] = useState<IProcessStatus | null>(null);
  const [optionsHedgerStatus, setOptionsHedgerStatus] = useState<IProcessStatus | null>(null);
  const [isLoadingPerpMm1, setIsLoadingPerpMm1] = useState(false);
  const [isLoadingPerpMm2, setIsLoadingPerpMm2] = useState(false);
  const [isLoadingPerpHedger1, setIsLoadingPerpHedger1] = useState(false);
  const [isLoadingPerpHedger2, setIsLoadingPerpHedger2] = useState(false);
  const [isLoadingOptionsMm, setIsLoadingOptionsMm] = useState(false);
  const [isLoadingOptionsHedger, setIsLoadingOptionsHedger] = useState(false);
  const [perpInstrumentFilter, setPerpInstrumentFilter] = useState<string>("");
  const [perpDeltaDisplayUnit, setPerpDeltaDisplayUnit] = useState<"usd" | "contracts">("usd");
  const [optionsInstrumentFilter, setOptionsInstrumentFilter] = useState<string>("");

  // Fetch Binance and Bybit positions for perps hedger account
  const {
    data: binancePositionsData = [],
    isLoading: isBinancePositionLoading,
    isValidating: isBinancePositionValidating,
  } = useBinancePositions(INTERNAL_PERPS_SHORT_ADDRESS);

  const {
    data: bybitPositionsData = [],
    isLoading: isBybitPositionLoading,
    isValidating: isBybitPositionValidating,
  } = useBybitPositions(INTERNAL_PERPS_SHORT_ADDRESS);

  const {
    data: hyperliquidPositionsData = [],
    isLoading: isHyperliquidPositionLoading,
    isValidating: isHyperliquidPositionValidating,
  } = useHyperliquidPositions(INTERNAL_PERPS_SHORT_ADDRESS);

  const lighterRobinhood = useLighterPositions("robinhood");
  const lighterMainnet = useLighterPositions("mainnet");
  const lighterComplete = lighterRobinhood.available && lighterMainnet.available;
  const { data: lighterMainnetPositionsData = [] } = lighterMainnet;
  const {
    data: lighterPositionsData = [],
    isLoading: isLighterPositionLoading,
    isValidating: isLighterPositionValidating,
  } = lighterRobinhood;

  // Fetch Aevo positions for both perps accounts
  const {
    data: perpsShortAccount,
    isLoading: isPerpsShortLoading,
    isValidating: isPerpsShortValidating,
  } = useAccount(INTERNAL_PERPS_SHORT_ADDRESS);

  const {
    data: perpsLongAccount,
    isLoading: isPerpsLongLoading,
    isValidating: isPerpsLongValidating,
  } = useAccount(INTERNAL_PERPS_LONG_ADDRESS);

  // Fetch Aevo positions for options account
  const {
    data: optionsAccount,
    isLoading: isOptionsLoading,
    isValidating: isOptionsValidating,
  } = useAccount(INTERNAL_OPTIONS_ADDRESS);

  // Fetch Deribit positions for options account (multiple assets)
  const { data: deribitBtcPositions = [] } = useDeribitPositions("BTC", INTERNAL_OPTIONS_ADDRESS);
  const { data: deribitEthPositions = [] } = useDeribitPositions("ETH", INTERNAL_OPTIONS_ADDRESS);
  const { data: deribitSolPositions = [] } = useDeribitPositions("SOL", INTERNAL_OPTIONS_ADDRESS);

  // Fetch Deribit account data for options account (multiple assets)
  const { data: deribitBtcAccount } = useDeribitAccount("BTC", INTERNAL_OPTIONS_ADDRESS);
  const { data: deribitEthAccount } = useDeribitAccount("ETH", INTERNAL_OPTIONS_ADDRESS);
  const { data: deribitSolAccount } = useDeribitAccount("SOL", INTERNAL_OPTIONS_ADDRESS);

  // Prepare positions for PerpAggregatedPositionTable
  const perpPositions = useMemo(() => {
    const binancePositions = standardizeBinancePositions(binancePositionsData);
    const bybitPositions = standardizeBybitPositions(bybitPositionsData);
    const hyperliquidPositions = standardizeHyperliquidPositions(hyperliquidPositionsData);
    const lighterPositions = standardizeLighterPositions(lighterPositionsData);
    const lighterMainnetPositions = standardizeLighterPositions(lighterMainnetPositionsData, "mainnet");
    const perpsShortPositions = standardizeAevoPositions(perpsShortAccount?.positions || [], undefined);
    const perpsLongPositions = standardizeAevoPositions(perpsLongAccount?.positions || [], undefined);

    // Combine Aevo short and long positions
    const aevoPositions = aggregatePositions([...perpsShortPositions, ...perpsLongPositions]);

    return {
      aevoPositions,
      binancePositions,
      bybitPositions,
      hyperliquidPositions,
      lighterPositions,
      lighterMainnetPositions,
    };
  }, [
    binancePositionsData,
    bybitPositionsData,
    hyperliquidPositionsData,
    lighterPositionsData,
    lighterMainnetPositionsData,
    perpsShortAccount,
    perpsLongAccount,
  ]);

  const isAggregatedLoading = useMemo(
    () =>
      isBinancePositionLoading ||
      isBybitPositionLoading ||
      isPerpsShortLoading ||
      isPerpsLongLoading ||
      isHyperliquidPositionLoading ||
      isLighterPositionLoading ||
      isBinancePositionValidating ||
      isBybitPositionValidating ||
      isPerpsShortValidating ||
      isPerpsLongValidating ||
      isHyperliquidPositionValidating ||
      isLighterPositionValidating,
    [
      isBinancePositionLoading,
      isBybitPositionLoading,
      isPerpsShortLoading,
      isPerpsLongLoading,
      isHyperliquidPositionLoading,
      isLighterPositionLoading,
      isBinancePositionValidating,
      isBybitPositionValidating,
      isPerpsShortValidating,
      isPerpsLongValidating,
      isHyperliquidPositionValidating,
      isLighterPositionValidating,
    ]
  );

  // Prepare options positions
  const optionsPositions = useMemo(() => {
    const aevoPositions = optionsAccount?.positions || [];

    // Combine all Deribit positions from different assets
    const deribitPositions = [
      ...deribitBtcPositions,
      ...deribitEthPositions,
      ...deribitSolPositions,
    ];

    return {
      aevoPositions,
      deribitPositions,
    };
  }, [optionsAccount, deribitBtcPositions, deribitEthPositions, deribitSolPositions]);

  // Prepare Deribit account data
  const deribitAccountData = useMemo(() => ({
      BTC: deribitBtcAccount,
      ETH: deribitEthAccount,
      SOL: deribitSolAccount,
    }), [deribitBtcAccount, deribitEthAccount, deribitSolAccount]);

  const isOptionsAggregatedLoading = useMemo(
    () => isOptionsLoading || isOptionsValidating,
    [isOptionsLoading, isOptionsValidating]
  );

  const fetchPerpMm1Status = useCallback(async (silent: boolean = true) => {
    setIsLoadingPerpMm1(true);
    const status = await getProcessStatus("perp_mm", selectedStatusEnv, "1", silent);
    if (status) {
      setPerpMm1Status(status);
    }
    setIsLoadingPerpMm1(false);
  }, [getProcessStatus, selectedStatusEnv]);

  const fetchPerpMm2Status = useCallback(async (silent: boolean = true) => {
    setIsLoadingPerpMm2(true);
    const status = await getProcessStatus("perp_mm", selectedStatusEnv, "2", silent);
    if (status) {
      setPerpMm2Status(status);
    }
    setIsLoadingPerpMm2(false);
  }, [getProcessStatus, selectedStatusEnv]);

  const fetchPerpHedger1Status = useCallback(async (silent: boolean = true) => {
    setIsLoadingPerpHedger1(true);
    const status = await getProcessStatus("perp_hedger", selectedStatusEnv, "1", silent);
    if (status) {
      setPerpHedger1Status(status);
    }
    setIsLoadingPerpHedger1(false);
  }, [getProcessStatus, selectedStatusEnv]);

  const fetchPerpHedger2Status = useCallback(async (silent: boolean = true) => {
    setIsLoadingPerpHedger2(true);
    const status = await getProcessStatus("perp_hedger", selectedStatusEnv, "2", silent);
    if (status) {
      setPerpHedger2Status(status);
    }
    setIsLoadingPerpHedger2(false);
  }, [getProcessStatus, selectedStatusEnv]);

  const fetchOptionsMmStatus = useCallback(async (silent: boolean = true) => {
    setIsLoadingOptionsMm(true);
    const status = await getProcessStatus("options_mm", selectedStatusEnv, undefined, silent);
    if (status) {
      setOptionsMmStatus(status);
    }
    setIsLoadingOptionsMm(false);
  }, [getProcessStatus, selectedStatusEnv]);

  const fetchOptionsHedgerStatus = useCallback(async (silent: boolean = true) => {
    setIsLoadingOptionsHedger(true);
    const status = await getProcessStatus("options_hedger", selectedStatusEnv, undefined, silent);
    if (status) {
      setOptionsHedgerStatus(status);
    }
    setIsLoadingOptionsHedger(false);
  }, [getProcessStatus, selectedStatusEnv]);

  // Fetch all statuses on component mount
  useEffect(() => {
    fetchPerpMm1Status();
    fetchPerpMm2Status();
    fetchPerpHedger1Status();
    fetchPerpHedger2Status();
    fetchOptionsMmStatus();
    fetchOptionsHedgerStatus();
  }, [fetchPerpMm1Status, fetchPerpMm2Status, fetchPerpHedger1Status, fetchPerpHedger2Status, fetchOptionsMmStatus, fetchOptionsHedgerStatus]);

  const rowStyle: React.CSSProperties = {
    display: "flex",
    gap: SPACING.two,
    alignItems: isMobileScreen ? "stretch" : "center",
    flexDirection: isMobileScreen ? "column" : "row",
    width: "100%",
  };

  return (
    <div
      style={{
        height: "calc(100vh - 60px)",
        overflow: "auto",
        paddingLeft: isMobileScreen ? SPACING.two : SPACING.eight * 3,
        paddingRight: isMobileScreen ? SPACING.two : SPACING.eight * 3,
        paddingTop: isMobileScreen ? SPACING.two : SPACING.four,
        paddingBottom: SPACING.four,
      }}
    >
      {isMobileScreen ? (
        <div style={{ marginBottom: SPACING.two }}>
          <MobileDirectory />
        </div>
      ) : null}

      {/* Positions Section */}
      <div style={{ marginBottom: SPACING.four }}>
        <h2>Perp Hedger Positions</h2>

        {/* Net Aggregated Positions */}
        <div style={{ marginBottom: SPACING.four }}>
          <div
            style={{
              padding: SPACING.three,
              backgroundColor: "#1a1a1a",
              borderRadius: "8px",
              marginBottom: SPACING.two,
            }}
          >
            <h3 style={{ marginBottom: SPACING.two }}>
              Net Positions (Aevo Short + Aevo Long + Binance + Bybit + Hyperliquid + Lighter Robinhood + Lighter Mainnet)
            </h3>
            <p style={{ fontSize: "14px", color: "#888", marginBottom: SPACING.two }}>
              Aggregated net positions across all perp hedger accounts and hedge venues
            </p>
          </div>
          <div
            style={{
              marginBottom: SPACING.two,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: SPACING.two,
            }}
          >
            <input
              type="text"
              value={perpInstrumentFilter}
              onChange={(e) => setPerpInstrumentFilter(e.target.value)}
              placeholder="Filter by Asset (e.g., ETH, BTC)"
              style={{
                padding: "8px 12px",
                fontSize: "14px",
                borderRadius: "8px",
                border: "1px solid #333",
                backgroundColor: "#1a1a1a",
                color: "#fff",
                minWidth: "250px",
              }}
            />
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: 4,
                borderRadius: "8px",
                backgroundColor: "#1a1a1a",
                border: "1px solid #333",
              }}
            >
              <span
                style={{
                  color: "#888",
                  fontSize: "12px",
                  paddingLeft: "8px",
                  paddingRight: "2px",
                }}
              >
                Delta view
              </span>
              <button
                type="button"
                onClick={() => setPerpDeltaDisplayUnit("usd")}
                style={{
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  backgroundColor: perpDeltaDisplayUnit === "usd" ? "#2a2a2a" : "transparent",
                  color: perpDeltaDisplayUnit === "usd" ? "#fff" : "#888",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                USD
              </button>
              <button
                type="button"
                onClick={() => setPerpDeltaDisplayUnit("contracts")}
                style={{
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  backgroundColor: perpDeltaDisplayUnit === "contracts" ? "#2a2a2a" : "transparent",
                  color: perpDeltaDisplayUnit === "contracts" ? "#fff" : "#888",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                Contracts
              </button>
            </div>
          </div>
          <LighterCoverage robinhood={lighterRobinhood} mainnet={lighterMainnet} />
          {lighterComplete && ((perpPositions.aevoPositions.length > 0 ||
            perpPositions.binancePositions.length > 0 ||
            perpPositions.bybitPositions.length > 0 ||
            perpPositions.hyperliquidPositions.length > 0 ||
            perpPositions.lighterPositions.length > 0 || perpPositions.lighterMainnetPositions.length > 0) ? (
              <PerpAggregatedPositionTable
                aggregatedAevoPositions={perpPositions.aevoPositions}
                aggregatedBinancePositions={perpPositions.binancePositions}
                aggregatedBybitPositions={perpPositions.bybitPositions}
                aggregatedHyperliquidPositions={perpPositions.hyperliquidPositions}
                aggregatedLighterPositions={perpPositions.lighterPositions}
                aggregatedLighterMainnetPositions={perpPositions.lighterMainnetPositions}
                selectedAsset={undefined}
                isLoading={isAggregatedLoading}
                instrumentFilter={perpInstrumentFilter}
                deltaDisplayUnit={perpDeltaDisplayUnit}
              />
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: SPACING.four,
                  color: "#888",
                  backgroundColor: "#1a1a1a",
                  borderRadius: "8px",
                }}
              >
                {isAggregatedLoading ? (
                  <Spinner />
                ) : (
                  "No positions found"
                )}
              </div>
            ))}
        </div>
      </div>

      {/* Visual Separator */}
      <div
        style={{
          height: "2px",
          background: "linear-gradient(to right, #333, #666, #333)",
          marginBottom: SPACING.four,
          marginTop: SPACING.four,
        }}
      />

      {/* Options Positions Section */}
      <div style={{ marginBottom: SPACING.four }}>
        <h2>Options Hedger Positions</h2>

        <OptionsGreeksSummaryTable
          aevoPositions={optionsPositions.aevoPositions}
          deribitPositions={optionsPositions.deribitPositions}
          deribitAccountData={deribitAccountData}
        />

        <div style={{ marginBottom: SPACING.four }}>
          <div
            style={{
              padding: SPACING.three,
              backgroundColor: "#1a1a1a",
              borderRadius: "8px",
              marginBottom: SPACING.two,
            }}
          >
            <h3 style={{ marginBottom: SPACING.two }}>
              Net Positions (Aevo + Deribit)
            </h3>
            <p style={{ fontSize: "14px", color: "#888", marginBottom: SPACING.two }}>
              Aggregated net options positions with net Greeks across Aevo and Deribit
            </p>
          </div>
          <div
            style={{
              marginBottom: SPACING.two,
              display: "flex",
              alignItems: "center",
              gap: SPACING.two,
            }}
          >
            <input
              type="text"
              value={optionsInstrumentFilter}
              onChange={(e) => setOptionsInstrumentFilter(e.target.value)}
              placeholder="Filter by Instrument Name"
              style={{
                padding: "8px 12px",
                fontSize: "14px",
                borderRadius: "8px",
                border: "1px solid #333",
                backgroundColor: "#1a1a1a",
                color: "#fff",
                minWidth: "250px",
              }}
            />
          </div>
          <OptionsAggregatedPositionTable
            aevoPositions={optionsPositions.aevoPositions}
            deribitPositions={optionsPositions.deribitPositions}
            isLoading={isOptionsAggregatedLoading}
            instrumentFilter={optionsInstrumentFilter}
            deribitAccountData={deribitAccountData}
          />
        </div>
      </div>

      {/* Visual Separator */}
      <div
        style={{
          height: "2px",
          background: "linear-gradient(to right, #333, #666, #333)",
          marginBottom: SPACING.four,
          marginTop: SPACING.four,
        }}
      />

      {/* Status Monitor Section */}
      <div style={{ marginBottom: SPACING.four }}>
        <h2>Status Monitor</h2>
        <div style={rowStyle}>
          <select
            value={selectedStatusEnv}
            onChange={(e) => setSelectedStatusEnv(e.target.value)}
            style={{
              width: isMobileScreen ? "100%" : 150,
              padding: "8px 12px",
              fontSize: "14px",
              borderRadius: "4px",
            }}
          >
            {ENV_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            onClick={() => {
              fetchPerpMm1Status(false);
              fetchPerpMm2Status(false);
              fetchPerpHedger1Status(false);
              fetchPerpHedger2Status(false);
              fetchOptionsMmStatus(false);
              fetchOptionsHedgerStatus(false);
            }}
            style={isMobileScreen ? { width: "100%" } : undefined}
          >
            Refresh All
          </Button>
        </div>
      </div>

      {/* Perp MM 1 Status Display */}
      <div style={{ marginBottom: SPACING.four, position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SPACING.two }}>
          <h3>Perp MM 1</h3>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            onClick={() => fetchPerpMm1Status(false)}
            disabled={isLoadingPerpMm1}
          >
            {isLoadingPerpMm1 ? "Loading..." : "Refresh"}
          </Button>
        </div>
        {isLoadingPerpMm1 && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              zIndex: 10,
            }}
          >
            <Spinner />
          </div>
        )}
        {perpMm1Status && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobileScreen ? "1fr" : "repeat(3, 1fr)",
                gap: SPACING.three,
                marginBottom: SPACING.three,
                padding: SPACING.three,
                backgroundColor: "#1a1a1a",
                borderRadius: "8px",
              }}
            >
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Total Assets
                </div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {perpMm1Status.total_assets}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Total Instances
                </div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {perpMm1Status.total_instances}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Status
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: perpMm1Status.assets.every((a) => a.status === "running")
                      ? "#4ade80"
                      : "#fbbf24",
                  }}
                >
                  {perpMm1Status.assets.every((a) => a.status === "running")
                    ? "All Running"
                    : "Partial"}
                </div>
              </div>
            </div>

            {/* Assets Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobileScreen
                  ? "repeat(2, 1fr)"
                  : "repeat(10, 1fr)",
                gap: SPACING.one,
                padding: SPACING.two,
                backgroundColor: "#0a0a0a",
                borderRadius: "8px",
              }}
            >
              {perpMm1Status.assets.map((assetStatus) => (
                <div
                  key={assetStatus.asset}
                  style={{
                    padding: "6px",
                    backgroundColor: "#1a1a1a",
                    borderRadius: "4px",
                    border: `1px solid ${
                      assetStatus.status === "running" ? "#4ade80" : "#ef4444"
                    }`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      marginBottom: "2px",
                    }}
                  >
                    {assetStatus.asset}
                  </div>
                  <div style={{ fontSize: "8px", color: "#888" }}>
                    {assetStatus.count} inst
                  </div>
                  <div
                    style={{
                      fontSize: "8px",
                      color: assetStatus.status === "running" ? "#4ade80" : "#ef4444",
                      marginTop: "1px",
                    }}
                  >
                    {assetStatus.status}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {!perpMm1Status && !isLoadingPerpMm1 && (
          <div style={{ textAlign: "center", padding: SPACING.four, color: "#888" }}>
            No data available. Click Refresh to load.
          </div>
        )}
      </div>

      {/* Perp MM 2 Status Display */}
      <div style={{ marginBottom: SPACING.four, position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SPACING.two }}>
          <h3>Perp MM 2</h3>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            onClick={() => fetchPerpMm2Status(false)}
            disabled={isLoadingPerpMm2}
          >
            {isLoadingPerpMm2 ? "Loading..." : "Refresh"}
          </Button>
        </div>
        {isLoadingPerpMm2 && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              zIndex: 10,
            }}
          >
            <Spinner />
          </div>
        )}
        {perpMm2Status && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobileScreen ? "1fr" : "repeat(3, 1fr)",
                gap: SPACING.three,
                marginBottom: SPACING.three,
                padding: SPACING.three,
                backgroundColor: "#1a1a1a",
                borderRadius: "8px",
              }}
            >
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Total Assets
                </div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {perpMm2Status.total_assets}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Total Instances
                </div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {perpMm2Status.total_instances}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Status
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: perpMm2Status.assets.every((a) => a.status === "running")
                      ? "#4ade80"
                      : "#fbbf24",
                  }}
                >
                  {perpMm2Status.assets.every((a) => a.status === "running")
                    ? "All Running"
                    : "Partial"}
                </div>
              </div>
            </div>

            {/* Assets Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobileScreen
                  ? "repeat(2, 1fr)"
                  : "repeat(10, 1fr)",
                gap: SPACING.one,
                padding: SPACING.two,
                backgroundColor: "#0a0a0a",
                borderRadius: "8px",
              }}
            >
              {perpMm2Status.assets.map((assetStatus) => (
                <div
                  key={assetStatus.asset}
                  style={{
                    padding: "6px",
                    backgroundColor: "#1a1a1a",
                    borderRadius: "4px",
                    border: `1px solid ${
                      assetStatus.status === "running" ? "#4ade80" : "#ef4444"
                    }`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      marginBottom: "2px",
                    }}
                  >
                    {assetStatus.asset}
                  </div>
                  <div style={{ fontSize: "8px", color: "#888" }}>
                    {assetStatus.count} inst
                  </div>
                  <div
                    style={{
                      fontSize: "8px",
                      color: assetStatus.status === "running" ? "#4ade80" : "#ef4444",
                      marginTop: "1px",
                    }}
                  >
                    {assetStatus.status}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {!perpMm2Status && !isLoadingPerpMm2 && (
          <div style={{ textAlign: "center", padding: SPACING.four, color: "#888" }}>
            No data available. Click Refresh to load.
          </div>
        )}
      </div>

      {/* Perp Hedger 1 Status Display */}
      <div style={{ marginBottom: SPACING.four, position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SPACING.two }}>
          <h3>Perp Hedger 1</h3>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            onClick={() => fetchPerpHedger1Status(false)}
            disabled={isLoadingPerpHedger1}
          >
            {isLoadingPerpHedger1 ? "Loading..." : "Refresh"}
          </Button>
        </div>
        {isLoadingPerpHedger1 && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              zIndex: 10,
            }}
          >
            <Spinner />
          </div>
        )}
        {perpHedger1Status && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobileScreen ? "1fr" : "repeat(3, 1fr)",
                gap: SPACING.three,
                marginBottom: SPACING.three,
                padding: SPACING.three,
                backgroundColor: "#1a1a1a",
                borderRadius: "8px",
              }}
            >
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Total Assets
                </div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {perpHedger1Status.total_assets}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Total Instances
                </div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {perpHedger1Status.total_instances}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Status
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: perpHedger1Status.assets.every((a) => a.status === "running")
                      ? "#4ade80"
                      : "#fbbf24",
                  }}
                >
                  {perpHedger1Status.assets.every((a) => a.status === "running")
                    ? "All Running"
                    : "Partial"}
                </div>
              </div>
            </div>

            {/* Assets Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobileScreen
                  ? "repeat(2, 1fr)"
                  : "repeat(10, 1fr)",
                gap: SPACING.one,
                padding: SPACING.two,
                backgroundColor: "#0a0a0a",
                borderRadius: "8px",
              }}
            >
              {perpHedger1Status.assets.map((assetStatus) => (
                <div
                  key={assetStatus.asset}
                  style={{
                    padding: "6px",
                    backgroundColor: "#1a1a1a",
                    borderRadius: "4px",
                    border: `1px solid ${
                      assetStatus.status === "running" ? "#4ade80" : "#ef4444"
                    }`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      marginBottom: "2px",
                    }}
                  >
                    {assetStatus.asset}
                  </div>
                  <div style={{ fontSize: "8px", color: "#888" }}>
                    {assetStatus.count} inst
                  </div>
                  <div
                    style={{
                      fontSize: "8px",
                      color: assetStatus.status === "running" ? "#4ade80" : "#ef4444",
                      marginTop: "1px",
                    }}
                  >
                    {assetStatus.status}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {!perpHedger1Status && !isLoadingPerpHedger1 && (
          <div style={{ textAlign: "center", padding: SPACING.four, color: "#888" }}>
            No data available. Click Refresh to load.
          </div>
        )}
      </div>

      {/* Perp Hedger 2 Status Display */}
      <div style={{ marginBottom: SPACING.four, position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SPACING.two }}>
          <h3>Perp Hedger 2</h3>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            onClick={() => fetchPerpHedger2Status(false)}
            disabled={isLoadingPerpHedger2}
          >
            {isLoadingPerpHedger2 ? "Loading..." : "Refresh"}
          </Button>
        </div>
        {isLoadingPerpHedger2 && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              zIndex: 10,
            }}
          >
            <Spinner />
          </div>
        )}
        {perpHedger2Status && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobileScreen ? "1fr" : "repeat(3, 1fr)",
                gap: SPACING.three,
                marginBottom: SPACING.three,
                padding: SPACING.three,
                backgroundColor: "#1a1a1a",
                borderRadius: "8px",
              }}
            >
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Total Assets
                </div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {perpHedger2Status.total_assets}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Total Instances
                </div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {perpHedger2Status.total_instances}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Status
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: perpHedger2Status.assets.every((a) => a.status === "running")
                      ? "#4ade80"
                      : "#fbbf24",
                  }}
                >
                  {perpHedger2Status.assets.every((a) => a.status === "running")
                    ? "All Running"
                    : "Partial"}
                </div>
              </div>
            </div>

            {/* Assets Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobileScreen
                  ? "repeat(2, 1fr)"
                  : "repeat(10, 1fr)",
                gap: SPACING.one,
                padding: SPACING.two,
                backgroundColor: "#0a0a0a",
                borderRadius: "8px",
              }}
            >
              {perpHedger2Status.assets.map((assetStatus) => (
                <div
                  key={assetStatus.asset}
                  style={{
                    padding: "6px",
                    backgroundColor: "#1a1a1a",
                    borderRadius: "4px",
                    border: `1px solid ${
                      assetStatus.status === "running" ? "#4ade80" : "#ef4444"
                    }`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      marginBottom: "2px",
                    }}
                  >
                    {assetStatus.asset}
                  </div>
                  <div style={{ fontSize: "8px", color: "#888" }}>
                    {assetStatus.count} inst
                  </div>
                  <div
                    style={{
                      fontSize: "8px",
                      color: assetStatus.status === "running" ? "#4ade80" : "#ef4444",
                      marginTop: "1px",
                    }}
                  >
                    {assetStatus.status}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {!perpHedger2Status && !isLoadingPerpHedger2 && (
          <div style={{ textAlign: "center", padding: SPACING.four, color: "#888" }}>
            No data available. Click Refresh to load.
          </div>
        )}
      </div>

      {/* Options MM Status Display */}
      <div style={{ marginBottom: SPACING.four, position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SPACING.two }}>
          <h3>Options MM</h3>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            onClick={() => fetchOptionsMmStatus(false)}
            disabled={isLoadingOptionsMm}
          >
            {isLoadingOptionsMm ? "Loading..." : "Refresh"}
          </Button>
        </div>
        {isLoadingOptionsMm && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              zIndex: 10,
            }}
          >
            <Spinner />
          </div>
        )}
        {optionsMmStatus && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobileScreen ? "1fr" : "repeat(3, 1fr)",
                gap: SPACING.three,
                marginBottom: SPACING.three,
                padding: SPACING.three,
                backgroundColor: "#1a1a1a",
                borderRadius: "8px",
              }}
            >
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Total Assets
                </div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {optionsMmStatus.total_assets}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Total Instances
                </div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {optionsMmStatus.total_instances}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Status
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: optionsMmStatus.assets.every((a) => a.status === "running")
                      ? "#4ade80"
                      : "#fbbf24",
                  }}
                >
                  {optionsMmStatus.assets.every((a) => a.status === "running")
                    ? "All Running"
                    : "Partial"}
                </div>
              </div>
            </div>

            {/* Assets Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobileScreen
                  ? "repeat(2, 1fr)"
                  : "repeat(10, 1fr)",
                gap: SPACING.one,
                padding: SPACING.two,
                backgroundColor: "#0a0a0a",
                borderRadius: "8px",
              }}
            >
              {optionsMmStatus.assets.map((assetStatus) => (
                <div
                  key={assetStatus.asset}
                  style={{
                    padding: "6px",
                    backgroundColor: "#1a1a1a",
                    borderRadius: "4px",
                    border: `1px solid ${
                      assetStatus.status === "running" ? "#4ade80" : "#ef4444"
                    }`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      marginBottom: "2px",
                    }}
                  >
                    {assetStatus.asset}
                  </div>
                  <div style={{ fontSize: "8px", color: "#888" }}>
                    {assetStatus.count} inst
                  </div>
                  <div
                    style={{
                      fontSize: "8px",
                      color: assetStatus.status === "running" ? "#4ade80" : "#ef4444",
                      marginTop: "1px",
                    }}
                  >
                    {assetStatus.status}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {!optionsMmStatus && !isLoadingOptionsMm && (
          <div style={{ textAlign: "center", padding: SPACING.four, color: "#888" }}>
            No data available. Click Refresh to load.
          </div>
        )}
      </div>

      {/* Options Hedger Status Display */}
      <div style={{ marginBottom: SPACING.four, position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SPACING.two }}>
          <h3>Options Hedger</h3>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            onClick={() => fetchOptionsHedgerStatus(false)}
            disabled={isLoadingOptionsHedger}
          >
            {isLoadingOptionsHedger ? "Loading..." : "Refresh"}
          </Button>
        </div>
        {isLoadingOptionsHedger && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              zIndex: 10,
            }}
          >
            <Spinner />
          </div>
        )}
        {optionsHedgerStatus && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobileScreen ? "1fr" : "repeat(3, 1fr)",
                gap: SPACING.three,
                marginBottom: SPACING.three,
                padding: SPACING.three,
                backgroundColor: "#1a1a1a",
                borderRadius: "8px",
              }}
            >
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Total Assets
                </div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {optionsHedgerStatus.total_assets}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Total Instances
                </div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {optionsHedgerStatus.total_instances}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "14px", color: "#888", marginBottom: "4px" }}>
                  Status
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: optionsHedgerStatus.assets.every((a) => a.status === "running")
                      ? "#4ade80"
                      : "#fbbf24",
                  }}
                >
                  {optionsHedgerStatus.assets.every((a) => a.status === "running")
                    ? "All Running"
                    : "Partial"}
                </div>
              </div>
            </div>

            {/* Assets Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobileScreen
                  ? "repeat(2, 1fr)"
                  : "repeat(10, 1fr)",
                gap: SPACING.one,
                padding: SPACING.two,
                backgroundColor: "#0a0a0a",
                borderRadius: "8px",
              }}
            >
              {optionsHedgerStatus.assets.map((assetStatus) => (
                <div
                  key={assetStatus.asset}
                  style={{
                    padding: "6px",
                    backgroundColor: "#1a1a1a",
                    borderRadius: "4px",
                    border: `1px solid ${
                      assetStatus.status === "running" ? "#4ade80" : "#ef4444"
                    }`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      marginBottom: "2px",
                    }}
                  >
                    {assetStatus.asset}
                  </div>
                  <div style={{ fontSize: "8px", color: "#888" }}>
                    {assetStatus.count} inst
                  </div>
                  <div
                    style={{
                      fontSize: "8px",
                      color: assetStatus.status === "running" ? "#4ade80" : "#ef4444",
                      marginTop: "1px",
                    }}
                  >
                    {assetStatus.status}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {!optionsHedgerStatus && !isLoadingOptionsHedger && (
          <div style={{ textAlign: "center", padding: SPACING.four, color: "#888" }}>
            No data available. Click Refresh to load.
          </div>
        )}
      </div>

      {/* <div style={{ marginBottom: SPACING.four }}>
        <h2>Trading Parameters</h2>
        <FilterWrapper style={{ marginBottom: SPACING.three }}>
          <MarketDropdown onClick={setTradingParamsAsset} />
        </FilterWrapper>
        {isValidating && !parametersData ? (
          <Spinner />
        ) : (
          <SettingsGrid>
            {filteredParameters?.map((parameters) => (
              <TradingParametersTable
                key={parameters.asset}
                parameters={parameters}
                updateParameters={updateTradingParameters}
              />
            ))}
          </SettingsGrid>
        )}
      </div> */}
    </div>
  );
}
